import { forwardRef, useEffect } from 'react';
import type { PropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';

import type { RefElement, WithAttributes } from '../../types';
import { useBreakpoint, useConsolidatedRef, useI18n, usePrevious } from '../../hooks';
import Grid from '../Grid';
import { StyledLink } from '../Link';
import { getActiveElement, getFocusables } from '../../utils';
import Flex from '../Flex';
import Button from '../Button';
import { defaultThemeProp } from '../../theme';
import { calculateFontSize, type FontSize } from '../../styles';
import { ellipsisOverflow } from '../../styles/mixins';

import type { FileDisplayProps } from './FileDisplay';
import type { FileItemProps } from './FileItem';
import FileDisplay from './FileDisplay';
import FileItem from './FileItem';

export interface CommonFileProps {
  onDownloadAll?: () => void;
}

export type FileListProps = WithAttributes<
  'div',
  | (CommonFileProps & {
      type: 'display';
      items: FileDisplayProps[];
      disabled?: never;
    })
  | (CommonFileProps & {
      type: 'item';
      items: FileItemProps[];
      disabled?: boolean;
    })
>;

export const StyledFileList = styled(Grid)`
  ${StyledLink} + ${StyledLink} {
    margin-inline-start: unset;
  }
`;

const StyledDownloadAllButton = styled.button(
  ({
    theme: {
      components: { text },
      base: { 'font-size': fontSize, 'font-scale': fontScale, spacing }
    }
  }) => {
    const fontSizes = calculateFontSize(fontSize, fontScale);
    return css`
      font-size: ${fontSizes[text.secondary['font-size'] as FontSize]};
      ${ellipsisOverflow}
      flex-shrink: 1;
      width: fit-content;
      margin-inline-start: calc(0.5 * ${spacing});
      margin-block-end: calc(0.5 * ${spacing});
    `;
  }
);

StyledDownloadAllButton.defaultProps = defaultThemeProp;

const FileList = forwardRef<RefElement<FileListProps>, PropsWithoutRef<FileListProps>>(
  function FileList({ type, items, onDownloadAll, disabled = false, ...restProps }, ref) {
    const listRef = useConsolidatedRef(ref);
    const isSmallOrAbove = useBreakpoint('sm', { breakpointRef: listRef });
    const t = useI18n();

    const prevItems = usePrevious(items);
    const activeElementContained = listRef.current?.contains(getActiveElement());

    useEffect(() => {
      // Early exit when focus doesn't need to be grabbed
      if (
        items.length === 0 ||
        !activeElementContained ||
        listRef.current?.contains(getActiveElement())
      ) {
        return;
      }

      prevItems?.forEach((item, oldIndex) => {
        const newIndex = items.findIndex(newItem => {
          return item.id === newItem.id;
        });

        if (newIndex === -1 && listRef.current) {
          if (oldIndex === prevItems.length - 1) {
            // Move the focus to previous element when last element is removed
            getFocusables(listRef.current.children[items.length - 1])[0]?.focus();
          } else {
            // Otherwise, Move the focus to next element
            getFocusables(listRef.current.children[oldIndex])[0]?.focus();
          }
        }
      });
    }, [items]);

    return (
      <Flex container={{ direction: 'column', gap: 1 }}>
        <Grid
          {...restProps}
          ref={listRef}
          container={{ gap: 0.5, cols: isSmallOrAbove ? 'repeat(2, 1fr)' : '1fr' }}
          as={StyledFileList}
        >
          {type === 'display'
            ? items.map(item => <FileDisplay key={item.id} {...item} />)
            : items.map(item => <FileItem key={item.id} {...item} disabled={disabled} />)}
        </Grid>
        {onDownloadAll && items.length > 1 && (
          <Button as={StyledDownloadAllButton} onClick={onDownloadAll} variant='link'>
            {t('download_all')}
          </Button>
        )}
      </Flex>
    );
  }
);

export default FileList;
